/**
 * The one table that says what a stored JSON-Schema `type` string means.
 *
 * Stored schemas are written by several producers — the workflow builder, the
 * public API, Pipedream component metadata — so the same concept arrives under
 * more than one spelling. There are exactly two aliases:
 *
 * - `integer` → NUMBER. JSON Schema draft 2020-12 defines seven types; six have
 *   a {@link WorkflowParameterType} of their own and `integer` has no JS
 *   counterpart.
 * - Anything ending `[]` → ARRAY. Pipedream's component metadata writes its
 *   array types as `string[]`, `object[]`, and so on.
 *
 * This lives in `@nexus/types` rather than next to any one consumer because
 * splitting it is what broke: the backend normalized a parameter's own type
 * through a table that knew `integer` while narrowing its array `items` through
 * a predicate that did not, so an array-of-integer silently lost its item
 * schema. Two predicates over one type space always eventually disagree.
 *
 * **Resolve, do not rewrite.** Callers use this to UNDERSTAND a stored value,
 * not to normalize it on the way back to the database. Rewriting a stored
 * `integer` to `number` looks harmless and is not: the republish sync compares
 * stored and derived setups field by field, reads the changed spelling as a
 * changed shape, and discards the operator's prompts, values and ids for that
 * parameter. Normalization belongs at the point the schema is EMITTED to a
 * model provider, where nothing is persisted.
 */

import { type WorkflowDisplayTypeValue, WorkflowParameterType } from "./workflow.types";

const TYPE_BY_NAME: ReadonlyMap<string, WorkflowParameterType> = new Map<
  string,
  WorkflowParameterType
>([
  ...Object.values(WorkflowParameterType).map((type): [string, WorkflowParameterType] => [
    type,
    type
  ]),
  ["integer", WorkflowParameterType.NUMBER]
]);

/**
 * Resolve a stored type string, or `undefined` when it is not a name we know.
 *
 * Use this to decide whether a stored node is usable and what it means. Prefer
 * it over comparing against {@link WorkflowParameterType} values directly —
 * that comparison is the one that misses `integer`.
 */
export function resolveParameterType(value: unknown): WorkflowParameterType | undefined {
  if (typeof value !== "string") return undefined;

  const named = TYPE_BY_NAME.get(value);
  if (named !== undefined) return named;

  // Pipedream array notation: "string[]", "object[]", "integer[]", …
  return value.endsWith("[]") ? WorkflowParameterType.ARRAY : undefined;
}

/**
 * Resolve a stored type string, falling back to STRING.
 *
 * STRING is the one type every provider accepts for a shape that cannot be
 * read, so an unrecognised name degrades instead of producing a node no branch
 * knows how to fill.
 */
export function toParameterType(value: unknown): WorkflowParameterType {
  return resolveParameterType(value) ?? WorkflowParameterType.STRING;
}

/**
 * Resolve a stored type string for the frontend's DISPLAY type space.
 *
 * {@link WorkflowDisplayTypeValue} is wider than {@link WorkflowParameterType}:
 * it also carries `branch`, `unknown` and `variable`, which are UI-only members
 * that never reach the database and that {@link resolveParameterType} correctly
 * does not know. So the fallback here keeps the ORIGINAL value rather than
 * degrading to STRING — a `branch` node must stay a branch.
 *
 * Use it wherever a display component switches on a type that came from stored
 * schema data. Resolve at the point the value ENTERS the component or the tree,
 * never once per comparison: a switch with seven cases needs one resolved
 * subject, not seven resolved cases, and the one-subject form cannot drift.
 *
 * Generic over the input so a caller holding a {@link WorkflowDisplayTypeValue}
 * gets one back — no cast — while a caller holding a raw `string` keeps the
 * honest `| string` in its result rather than being told a lie.
 */
export function toDisplayType<T extends string>(value: T): WorkflowDisplayTypeValue | T {
  return resolveParameterType(value) ?? value;
}

/**
 * There is deliberately NO spelling-insensitive type comparison here.
 *
 * One is easy to write and looks like an obvious improvement: a stored
 * `integer` and a derived `number` are the same type, so the republish sync
 * should not read them as a changed shape and discard the operator's setup.
 *
 * It cannot be added alone. Making the sync tolerant means an aliased spelling
 * SURVIVES a publish instead of being normalized away by the REPLACE branch,
 * and roughly sixty sites downstream — the workflow execution engine, the tool
 * executor, the dashboard's parameter editors — still match type strings
 * exactly. Tolerating the alias here without resolving it there makes the
 * parameter survive the publish and then vanish at the next step, which is
 * strictly worse than losing the setup.
 *
 * The unification is worth doing, in a change that moves those consumers in the
 * same pass. Until then this module RESOLVES a type when reading one, and never
 * claims two spellings are interchangeable.
 */
